import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Shirt,
    Scissors,
    Sparkles,
    Compass,
    Layers,
    Zap,
    Sliders,
    Camera,
    Tv,
    Eye,
    Activity,
    Image as ImageIcon
} from 'lucide-react';

const fashionServices = [
    { id: '01', icon: Shirt, title: 'High-Fashion Editorial Layouts', desc: 'Crafting avant-garde, narrative-driven visual essays designed for print magazines and digital editorial features, focusing on bold styling and cinematic lighting.' },
    { id: '02', icon: Compass, title: 'Commercial Brand Lookbooks', desc: 'Clean, highly-styled asset suites shot in studio or on-location, engineered to showcase seasonal apparel collections while retaining strong brand identity.' },
    { id: '03', icon: Scissors, title: 'Avant-Garde Detail & Texture', desc: 'Macro focus capturing the precise geometry of garments—highlighting complex textiles, raw stitching patterns, luxury fabrics, and silhouette silhouettes.' },
    { id: '04', icon: Sparkles, title: 'Beauty & High-Concept Cosmetics', desc: 'Macro beauty portraits focusing on detailed skin textures, creative editorial cosmetics, and specialized hair styling under sharp studio lighting arrays.' },
    { id: '05', icon: Camera, title: 'Agency-Standard Model Digitals', desc: 'Clean, un-retouched, beautifully lit structural polaroids and headshots optimized directly for top-tier international modeling agency submissions.' },
    { id: '06', icon: Activity, title: 'Runway & Dynamic Motion Capture', desc: 'High-speed shutter calibration freezing clothes in rhythmic motion—perfectly trapping the fluid drape of garments during fluid steps or high-energy choreography.' },
];

const fashionDeliverables = [
    { id: '01', icon: Tv, title: 'On-Set Real-Time Tethered Displays', desc: 'Instant file rendering directly to calibrated large-format iPads or studio displays so creative directors can audit styling choices instantly.' },
    { id: '02', icon: Sliders, title: 'Advanced High-End Skin Retouching', desc: 'Utilizing non-destructive frequency separation to clean transient blemishes while preserving authentic skin grain, pore structures, and organic shadows.' },
    { id: '03', icon: Shirt, title: 'True-to-Life Fabric Color Grading', desc: 'Calibrating files with precise capture charts to guarantee textile hues, complex patterns, and fabrics perfectly match real-world dye lots.' },
    { id: '04', icon: Layers, title: 'Creative Direction & Moodboard Build', desc: 'Mapping out comprehensive conceptual treatments, casting guides, location scouting paths, and lighting configurations before production.' },
    { id: '05', icon: Eye, title: 'High-Fashion Fine-Art Film Grain', desc: 'Infusing digital masters with organic, custom-built photographic grain textures to emulate timeless 35mm and medium-format film aesthetics.' },
    { id: '06', icon: Zap, title: 'Precision High-Speed Strobe Sync', desc: 'Deploying studio power packs with hyper-short flash durations to trap dynamic leaps, running models, and flying fabric without ghosting.' },
    { id: '07', icon: Compass, title: 'Editorial Location & Permit Management', desc: 'Handling rigorous municipal permissions, logistics coordination, and natural light tracking for high-profile destination outdoor campaigns.' },
    { id: '08', icon: Scissors, title: 'Digital Wardrobe Sizing Optimization', desc: 'Meticulous on-set checking and micro-retouching to fix unintended fabric bunches, label bulges, or loose structural pin lines gracefully.' },
    { id: '09', icon: ImageIcon, title: 'Redundant Production Master Repositories', desc: 'Securing massive raw multi-layer files across multiple offline and cloud arrays for instant billboard print or banner transformations.' },
];

const FashionPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.54;
        const speedRight = 0.34;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>

            <Helmet>
                <title>Fashion Photography Portfolio in Hyderabad | KhultStudios</title>

                <meta
                    name="description"
                    content="Explore KhultStudios' Fashion Photography Portfolio featuring editorial fashion shoots, designer collections, model portfolios, beauty photography, lookbooks, clothing brands, influencer campaigns, and creative fashion photography in Hyderabad."
                />

                <meta
                    name="keywords"
                    content="Fashion Photography Portfolio Hyderabad, Fashion Photography Hyderabad, Fashion Photographer Hyderabad, Editorial Fashion Photography, Designer Photography, Fashion Model Portfolio, Beauty Photography Hyderabad, Lookbook Photography, Clothing Brand Photography, Apparel Photography, Influencer Photography, Makeup Photography, Commercial Fashion Photography, Fashion Campaign Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/fashion-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/fashion-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Fashion Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Browse our portfolio of editorial fashion photography, designer lookbooks, model portfolios, beauty campaigns, influencer shoots, and creative fashion photography."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/fashion-photography-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Fashion Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Discover our portfolio of fashion editorials, designer collections, beauty photography, and model portfolios in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/fashion-photography-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/fashion_editorial_reel.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        High-Concept Editorial & Brand Campaign Designers
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Visual Narratives to Define Trends
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        Fashion is not simply about documenting clothing; it is about conveying an attitude, a lifestyle, and a provocative narrative. Stale, flat studio captures reduce elite apparel design to mere fabric, killing consumer aspiration and brand prestige instantly.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we specialize in high-impact fashion visual design. By integrating advanced moodboard production, high-speed strobe motion freezing, and non-destructive editorial skin-texturing, we create striking campaigns that demand space in luxury editorials and market spaces alike.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Collections
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=500" alt="Avant Garde High Fashion Studio Editorial Model Portrait" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=500" alt="Outdoor Luxury Brand Lookbook Campaign Visual" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=500" alt="Apparel Texture Macro Silhouette Motion Profile" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1496440737103-cd596325d314?q=80&w=500" alt="High Contrast Creative Beauty Cosmestics Frame" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?q=80&w=500" alt="Avant Garde High Fashion Studio Editorial Model Portrait Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=500" alt="Outdoor Luxury Brand Lookbook Campaign Visual Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=500" alt="Apparel Texture Macro Silhouette Motion Profile Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1496440737103-cd596325d314?q=80&w=500" alt="High Contrast Creative Beauty Cosmestics Frame Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/fashion-photography/fashion-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* FASHION SECTOR SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">✨ Strategic Fashion Imagery Modules</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Different layout formats demand distinct lighting styles. Our intentional campaign tiers map directly onto printing standards, web e-commerce lookbooks, and high-prestige editorial spaces.
                    </p>
                    <div className="row g-4 mt-2">
                        {fashionServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TECHNICAL EXECUTION & PRODUCTION SECURE FRAMEWORKS */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Execution & On-Set Production Ecosystems</h2>
                    <div className="row g-4">
                        {fashionDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FashionPhotographyPortfolio;